import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Atom, FlaskConical, Zap, Lock, Beaker, Sun, Moon, GraduationCap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { CLASS_9_SCIENCE_CHAPTERS, SCIENCE_CLASSES } from '../../data/scienceChapters';

const branchIcon = (branch) => {
  if (branch === 'Physics') return Zap;
  if (branch === 'Chemistry') return FlaskConical;
  if (branch === 'Biology') return Beaker;
  return Atom;
};

const branchColor = {
  Physics:   { ring: 'from-cyan-500/30 to-blue-500/30',     text: 'text-cyan-600 dark:text-cyan-400',   border: 'border-cyan-500/30' },
  Chemistry: { ring: 'from-amber-500/30 to-orange-500/30',  text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-500/30' },
  Biology:   { ring: 'from-emerald-500/30 to-teal-500/30',  text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-500/30' },
};

// Theme key shared across all Class 9-12 science pages
export const SCIENCE_THEME_KEY = 'practiq_science_theme';

const Class9SciencePage = () => {
  const navigate = useNavigate();
  const [theme, setTheme] = useState(() => localStorage.getItem(SCIENCE_THEME_KEY) || 'light');
  useEffect(() => { localStorage.setItem(SCIENCE_THEME_KEY, theme); }, [theme]);
  const isLight = theme === 'light';

  const cls = useMemo(() => ({
    page:    isLight ? 'bg-gray-50 text-gray-900' : 'bg-[#0A0F1C] text-white',
    topbar:  isLight ? 'bg-white/80 backdrop-blur border-gray-200' : 'bg-[#0F1626]/40 backdrop-blur border-white/5',
    card:    isLight ? 'bg-white border-gray-200 hover:border-gray-300' : 'bg-[#0F1626] border-white/10 hover:border-white/20',
    softBg:  isLight ? 'bg-gray-100' : 'bg-white/[0.02]',
    softBgInner: isLight ? 'bg-gray-50 border-gray-200' : 'bg-white/[0.02] border-white/5',
    mutedText: isLight ? 'text-gray-500' : 'text-gray-500',
    softText:  isLight ? 'text-gray-700' : 'text-gray-400',
    label:     isLight ? 'text-gray-400' : 'text-gray-600',
    backBtn:   isLight ? 'text-gray-600 hover:text-teal-600' : 'text-gray-400 hover:text-teal-400',
    tabBar:    isLight ? 'bg-gray-100 border-gray-200' : 'bg-white/[0.03] border-white/10',
    tabIdle:   isLight ? 'text-gray-500 hover:text-gray-900 hover:bg-white' : 'text-gray-400 hover:text-white hover:bg-white/5',
    chapterPreview: isLight ? 'bg-gray-50 border-gray-200' : 'bg-white/[0.02] border-white/5',
    promoBorder: isLight ? 'border-amber-300' : 'border-amber-500/20',
    promoBg:   isLight ? 'bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50' : 'bg-gradient-to-r from-amber-500/[0.05] via-orange-500/[0.05] to-amber-500/[0.05]',
    disabledBtn: isLight ? 'bg-gray-200 text-gray-400 border-gray-300' : 'bg-white/5 text-gray-500 border-white/10',
  }), [isLight]);

  const liveCount = CLASS_9_SCIENCE_CHAPTERS.filter(c => c.simulator.status === 'live').length;

  return (
    <div className={`min-h-screen ${cls.page}`} data-testid="class-9-science-page">
      {/* Top bar */}
      <div className={`border-b ${cls.topbar} sticky top-0 z-30`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className={`inline-flex items-center gap-2 text-sm ${cls.backBtn} transition-colors`} data-testid="back-home">
            <ArrowLeft className="w-4 h-4" /> Back to PractiQ
          </button>
          <div className="flex items-center gap-2">
            <span className={`text-xs ${cls.mutedText} hidden sm:inline-flex items-center gap-1.5`}>
              <GraduationCap className="w-3.5 h-3.5 text-teal-500" /> Science Lab
            </span>
            <button
              onClick={() => setTheme(t => t === 'light' ? 'dark' : 'light')}
              className={`p-2 rounded-lg ${isLight ? 'hover:bg-gray-100 text-gray-600' : 'hover:bg-white/5 text-gray-400'} transition-colors`}
              title={`Switch to ${isLight ? 'dark' : 'light'} mode`}
              data-testid="theme-toggle"
            >
              {isLight ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-7xl mx-auto px-6 pt-12 pb-8">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <Badge className={`${isLight ? 'bg-teal-100 text-teal-700 border-teal-200' : 'bg-teal-500/15 text-teal-400 border-teal-500/30'} text-xs px-3 py-1`}>CBSE / NCERT</Badge>
          <Badge className={`${isLight ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'} text-xs px-3 py-1`}>{liveCount} Live · {CLASS_9_SCIENCE_CHAPTERS.length - liveCount} Coming Soon</Badge>
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-3">
          Class <span className="bg-gradient-to-r from-teal-500 to-cyan-500 bg-clip-text text-transparent">9 Science</span> Simulators
        </h1>
        <p className={`text-base ${cls.softText} max-w-2xl`}>
          Visual, interactive simulations for every NCERT Science chapter. Drag, click, experiment — and watch the concepts come alive.
        </p>

        {/* Class tabs */}
        <div className={`mt-8 inline-flex items-center gap-1 p-1 rounded-xl border ${cls.tabBar}`}>
          {SCIENCE_CLASSES.map(c => (
            <button
              key={c.id}
              onClick={() => c.status === 'live' ? navigate(c.route) : null}
              disabled={c.status !== 'live'}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                c.id === 9
                  ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-sm'
                  : c.status === 'live'
                    ? cls.tabIdle
                    : (isLight ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 cursor-not-allowed')
              }`}
              data-testid={`tab-class-${c.id}`}
            >
              Class {c.id}
              {c.status !== 'live' && <span className="ml-2 text-[10px] text-amber-500">Soon</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Chapter Grid */}
      <div className="max-w-7xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {CLASS_9_SCIENCE_CHAPTERS.map((ch) => {
            const Icon = branchIcon(ch.branch);
            const colors = branchColor[ch.branch] || branchColor.Physics;
            const isLive = ch.simulator.status === 'live';
            return (
              <div
                key={ch.id}
                className={`group ${cls.card} border rounded-2xl p-5 transition-all flex flex-col shadow-sm hover:shadow-md`}
                data-testid={`chapter-card-${ch.id}`}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colors.ring} border ${colors.border} flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 ${colors.text}`} />
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge variant="outline" className={`${colors.border} ${colors.text} bg-transparent text-[10px]`}>{ch.branch}</Badge>
                    {isLive ? (
                      <Badge className={`${isLight ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'} text-[10px]`}>Live</Badge>
                    ) : (
                      <Badge className={`${isLight ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-amber-500/15 text-amber-400 border-amber-500/30'} text-[10px] inline-flex items-center gap-1`}>
                        <Lock className="w-2.5 h-2.5" /> Coming Soon
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Title */}
                <div className="mb-2">
                  <p className={`text-[11px] ${cls.label} uppercase tracking-wider`}>Chapter {ch.number}</p>
                  <h3 className="text-lg font-semibold mt-0.5 leading-tight">{ch.name}</h3>
                </div>

                {/* Description */}
                <p className={`text-xs ${cls.mutedText} mb-4 leading-relaxed`}>{ch.description}</p>

                {/* Learning goals */}
                <div className="mb-4">
                  <p className={`text-[10px] ${cls.label} uppercase tracking-wider mb-2`}>What you&apos;ll learn</p>
                  <ul className="space-y-1">
                    {ch.learningGoals.map((g, i) => (
                      <li key={i} className={`text-xs ${cls.softText} flex items-start gap-1.5`}>
                        <span className={`mt-1 w-1 h-1 rounded-full ${colors.text} bg-current flex-shrink-0`}></span>
                        <span>{g}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Preview line */}
                <div className={`${cls.softBgInner} border rounded-lg p-3 mb-4 flex-1`}>
                  <p className={`text-[10px] ${cls.label} uppercase tracking-wider mb-1`}>Simulator</p>
                  <p className={`text-xs ${cls.softText} leading-relaxed`}>{ch.simulator.preview}</p>
                </div>

                {/* CTA */}
                {isLive ? (
                  <Button
                    onClick={() => navigate(ch.simulator.route)}
                    className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white"
                    data-testid={`launch-${ch.id}`}
                  >
                    {ch.simulator.cta}
                    <Atom className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button disabled className={`w-full ${cls.disabledBtn} cursor-not-allowed border`}>
                    <Lock className="w-4 h-4 mr-2" />
                    {ch.simulator.cta}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* Coming-soon classes strip */}
        <div className={`mt-12 ${cls.promoBg} border ${cls.promoBorder} rounded-2xl p-6`}>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <p className="text-[11px] text-amber-600 uppercase tracking-widest font-medium mb-1">Coming Soon</p>
              <h3 className="text-lg font-semibold">Class 10, 11 &amp; 12 Science simulators</h3>
              <p className={`text-sm ${cls.softText} mt-1`}>We&apos;re building Physics, Chemistry &amp; Biology simulators for every NCERT chapter. Subscribe to be notified.</p>
            </div>
            <div className="flex items-center gap-3">
              {SCIENCE_CLASSES.filter(c => c.status !== 'live').map(c => (
                <div key={c.id} className="text-center">
                  <p className="text-2xl font-bold text-amber-600">Class {c.id}</p>
                  <p className={`text-[10px] ${cls.mutedText} uppercase tracking-wider`}>{c.chapterCount} chapters</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className={`text-center text-[11px] ${cls.label} mt-10`}>
          Syllabus aligned with{' '}
          <Link to="/" className={`${cls.mutedText} hover:text-teal-500`}>CBSE / NCERT Class 9 Science</Link>
        </p>
      </div>
    </div>
  );
};

export default Class9SciencePage;
